import {Component, OnInit, OnDestroy, Input, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import { ICategoryTarget } from '../../irb-editors.model';
import { UtilityService } from '../../../irb-common/services/utility.service';
import { EditorModelService } from '../../editor-model/editor-model.service';
import { AppService } from '@irb/app.service';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';

@Component({ standalone: false,
  selector: 'irb-mmc-allocations-chart',
  templateUrl: './mmc-allocations-chart.component.html',
  styleUrls: ['./mmc-allocations-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MmcAllocationsChartComponent implements OnInit, OnDestroy {

  chartData: any;
  categoryTargetPercent: Array<ICategoryTarget> = [];
  colors: Array<string>;
  private editDataChangeSubscription: Subscription;
  objectOfCategoryLists: any = {};

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const decimal = this.modelType === 'Blended' ? 2 : 1;
            const label = context.label;
            const value = Number(context.dataset.data[context.dataIndex]).toFixed(decimal);
            const retVal = this.noTarget ? label : label + " " + value + "%";
            return retVal;
          }
        }
      }
    }
  };

  @Input()
  modelType: string;

  @Input()
  categoryLabel: string = 'CustCat';

  @Input()
  noTarget: boolean = false;

  ENABLE_SEC_GRID_CHART_MMC: boolean = false;
  allUncategorized: boolean = false;
  sacLevel1Allocations: Array<ICategoryTarget> = [];
  sacLevel1ChartData: any;
  sacLevel1TableDataSource: MatTableDataSource<ICategoryTarget> = new MatTableDataSource([]);
  sacLevel1TableColumns: string[] = ['category', 'color'];

  categoryTableDataSource: MatTableDataSource<ICategoryTarget> = new MatTableDataSource([]);
  categoryTableColumns: string[] = ['category', 'color'];

  sacLevel1DoughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return context.label;
          }
        }
      }
    }
  };

  constructor(
    private util: UtilityService,
    private editorModelService: EditorModelService,
    private appService: AppService
  ) {
    this.ENABLE_SEC_GRID_CHART_MMC = this.appService.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_SEC_GRID_CHART_MMC || false;
  }

  ngOnInit(): void {
    this.update();

    this.editDataChangeSubscription = this.editorModelService.parametersChanged.subscribe(() => {
      this.colors = [];
      this.categoryTargetPercent = [];
      this.update();
    });
  }

  update() {
    this.colors = this.editorModelService.getChatColors();
    let categories = this.editorModelService.getCategories();
    let percentages = this.editorModelService.getPercentages();
    this.categoryTargetPercent = this.editorModelService.getCategoryTargetPercent();
    if (categories.length === 0) {
      this.colors = ['#93000F'];
      categories = ['Under-Allocated'];
      percentages = [100];
      this.categoryTargetPercent = [{ 'category': 'Under-Allocated', 'targetPercent': 100, colorCode: '#93000F'}];
      let lists: any = [];
      lists[0] = this.categoryTargetPercent;
      this.objectOfCategoryLists = {
        numOfLists: 1,
        lists: lists
      };
    } else {
      this.buildObjectOfCategoryLists();
    }
    this.categoryTableDataSource.data = this.categoryTargetPercent;
    this.checkAllUncategorized(categories);

    this.chartData = {
      labels: categories,
      datasets: [
        {
          data: percentages,
          backgroundColor: this.colors,
          hoverBackgroundColor: this.colors,
          borderWidth: 2,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };
    if (!this.noTarget) {
      const globSet: any = this.editorModelService.getGlobalSettings();
      if (globSet && globSet.securityCustAttrAbbr) {
        this.categoryLabel = globSet.securityCustAttrAbbr;
      }
    }

    this.buildSacLevel1Data();
  }

  private checkAllUncategorized(categories: string[]) {
    if (!this.ENABLE_SEC_GRID_CHART_MMC) {
      this.allUncategorized = false;
      return;
    }
    this.allUncategorized = categories.length === 1 && categories[0] === 'Uncategorized';
  }

  private buildSacLevel1Data() {
    if (!this.ENABLE_SEC_GRID_CHART_MMC) {
      return;
    }
    const allocations = this.editorModelService.getAllocationList();
    const hasAcData = allocations.some((a: any) => a.acLevel1 && a.acLevel1.length > 0);
    if (!hasAcData) {
      this.sacLevel1ChartData = null;
      this.sacLevel1TableDataSource.data = [];
      return;
    }
    this.sacLevel1Allocations = this.editorModelService.getGroupedAllocations('SAC1')
      .filter(item => item.category);
    if (this.sacLevel1Allocations && this.sacLevel1Allocations.length > 0) {
      this.sacLevel1TableDataSource.data = this.sacLevel1Allocations;
      const categories = this.sacLevel1Allocations.map(item => item.category);
      const percentages = this.sacLevel1Allocations.map(item => Number(item.targetPercent));
      const bgColors = this.sacLevel1Allocations.map(item => item.colorCode || '#BABABA');
      this.sacLevel1ChartData = {
        labels: categories,
        datasets: [{
          data: percentages,
          backgroundColor: bgColors,
          hoverBackgroundColor: bgColors,
          borderWidth: 2,
          hoverBorderColor: ['#FFFFFF']
        }]
      };
    } else {
      this.sacLevel1ChartData = null;
      this.sacLevel1TableDataSource.data = [];
    }
  }

  private buildObjectOfCategoryLists() {
    const copyData = JSON.parse(JSON.stringify(this.categoryTargetPercent));
    copyData.sort((a: any, b: any) => (Number(a.targetPercent) < Number(b.targetPercent)) ? 1 : -1);
    const numOfLists = Math.ceil(copyData.length / 10);
    let listsOfAll: any = [],
        singleList: any = [],
        startIdx: number,
        endIdx: number;
    for (let i = 0; i < numOfLists; i++) {
      startIdx = i * 10;
      endIdx = i * 10 + 10;
      singleList = copyData.slice(startIdx, endIdx);
      listsOfAll[i] = singleList;
    }
    this.objectOfCategoryLists = {
      numOfLists: 1,
      lists: listsOfAll
    };
  }

  getColorByCategory(category: string) {
    return this.editorModelService.getColorByCategory(category);
  }

  ngOnDestroy(): void {
    if (this.editDataChangeSubscription) {
      this.editDataChangeSubscription.unsubscribe();
    }
  }
}
